import { Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { NgIf } from '@angular/common';
import { SwUpdateService } from 'src/app/core/services/sw-update.service';

/** CSS variable the nav bar, `.sidebarview` and `.section` move down by (see styles.scss). */
export const UPDATE_STRIP_HEIGHT_VAR = '--app-update-strip-height';

/**
 * Full-width "New version available" band above the fixed nav bar, shown once a
 * new deploy is detected.
 *
 * A single action, "Refresh now", with no dismiss: a user who declines an update
 * is the user left on an old build. SwUpdateNavigationService also applies the
 * update on the next page navigation, so most users never need to click it.
 *
 * The nav bar is position:fixed, so the strip publishes its height as a CSS
 * variable and the nav bar, `.sidebarview` and `.section` move down by that much
 * instead of the strip covering them.
 */
@Component({
    selector: 'app-update-strip',
    imports: [NgIf],
    templateUrl: './update-strip.component.html',
    styleUrls: ['./update-strip.component.scss']
})
export class UpdateStripComponent implements OnDestroy {
  /** Latches on click so the button cannot fire twice while the page reloads. */
  refreshing = false;

  private resizeObserver?: ResizeObserver;

  constructor(public swUpdateService: SwUpdateService) {}

  /**
   * Tracks the strip's height while it is rendered.
   *
   * @param ref - The strip element, or undefined once it is removed.
   */
  @ViewChild('strip')
  set strip(ref: ElementRef<HTMLElement> | undefined) {
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
    const element = ref?.nativeElement;
    if (!element) {
      this.setHeight(0);
      return;
    }
    this.setHeight(element.offsetHeight);
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.setHeight(element.offsetHeight));
      this.resizeObserver.observe(element);
    }
  }

  /**
   * Applies the update. The service waits for any running API call or contract
   * transaction to finish before reloading.
   *
   * @returns Nothing.
   */
  refreshNow(): void {
    if (this.refreshing) { return; }
    this.refreshing = true;
    void this.swUpdateService.activateUpdateAndReload();
  }

  /**
   * @param height - The strip's rendered height in pixels (0 when hidden).
   * @returns Nothing.
   */
  private setHeight(height: number): void {
    document.documentElement.style.setProperty(UPDATE_STRIP_HEIGHT_VAR, `${height}px`);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.setHeight(0);
  }
}
